import { useCallback, useMemo, useState } from 'react'
import { Box, FormControlLabel, Grid, TextField, Typography } from '@mui/material'
import SimpleModal from '../../../../../components/modal/simpleModal'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import BaseInput from '../../../../../components/dinamicForm/baseInput'
import { IOSSwitch } from '../../../../../components/dinamicForm/switch'
import { DateModalInput } from '../../../../../components/dinamicForm/dateModal'
import { icons } from '../../../../../utils/icons'
import { onChange } from '../../../../../utils/types'
import { PLAN_INCLUDE_OPTIONS } from '../constants'
import { useStyles } from './step1BasicInfo.styles'
import { EvidenceData } from '@/hooks/evidences/typyng'
import { LinkedQuestionnaire, PlanEvidence, PlanIncludeOption, PlanItem, Step1BasicInfoProps } from '../types'
import ConfigureItems from './configureItems'
import ConfigureLinkQuestionnaire from './configureLinkQuestionnaire'
import ConfigureEvidences from './configureEvidences'
import ConfigureSolverEvidences from './configureSolverEvidences'

const Step1BasicInfo = ({
  data,
  errors,
  onChange,
  onClearOption,
  activeConfig,
  onEnterConfig,
  onExitConfig,
}: Step1BasicInfoProps) => {
  const { t } = useTranslation()
  const styles = useStyles()
  const [clearModalOption, setClearModalOption] = useState<PlanIncludeOption | null>(null)
  const minDate = useMemo(() => dayjs(), [])

  const handleOpenClearModal = (optionId: PlanIncludeOption) => {
    setClearModalOption(optionId)
  }

  const handleCloseClearModal = () => {
    setClearModalOption(null)
  }

  const handleConfirmClear = () => {
    if (clearModalOption) {
      onClearOption(clearModalOption)
    }
    setClearModalOption(null)
  }

  const handleTitleChange: onChange = (event) => {
    onChange({ title: event.target.value as string })
  }

  const handleInstructionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.slice(0, 300)
    onChange({ instructions: value })
  }

  const handleEndDateChange = (date: dayjs.Dayjs) => {
    onChange({ endDate: date.toDate() })
  }

  const handleSendEmailCopyChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange({ sendEmailCopy: checked })
  }

  const handleEnterConfig = (optionId: PlanIncludeOption) => {
    if (
      optionId === 'items' ||
      optionId === 'linkQuestionnaire' ||
      optionId === 'evidences' ||
      optionId === 'solverEvidences'
    ) {
      onEnterConfig(optionId)
    }
  }

  const isItemsConfigured =
    data.includes.includes('items') &&
    data.items.some((item) => item.selected && item.answer.trim())

  const isLinkQuestionnaireConfigured =
    data.includes.includes('linkQuestionnaire') &&
    !!data.linkQuestionnaire?.questionnaireId &&
    data.linkQuestionnaire.questionIds.length > 0

  const isEvidencesConfigured =
    data.includes.includes('evidences') &&
    data.evidences.some(
      (evidence) => evidence.files.length > 0 || evidence.comments.length > 0
    )

  const isSolverEvidencesConfigured =
    data.includes.includes('solverEvidences') &&
    data.solverEvidences.some((item) =>
      Object.values(item ?? {}).some((evidence) => evidence?.active)
    )

  const handleSaveItems = useCallback((items: PlanItem[]) => {
    const hasSelectedItems = items.some((item) => item.selected)
    const newIncludes = hasSelectedItems
      ? Array.from(new Set([...data.includes, 'items']))
      : data.includes.filter((id) => id !== 'items')

    onChange({ items, includes: newIncludes as PlanIncludeOption[] })
  }, [data.includes, onChange])

  const handleSaveLinkQuestionnaire = useCallback((linkQuestionnaire: LinkedQuestionnaire) => {
    const hasSelectedQuestions = linkQuestionnaire.questionIds.length > 0
    const newIncludes = hasSelectedQuestions
      ? Array.from(new Set([...data.includes, 'linkQuestionnaire']))
      : data.includes.filter((id) => id !== 'linkQuestionnaire')

    onChange({ linkQuestionnaire, includes: newIncludes as PlanIncludeOption[] })
  }, [data.includes, onChange])

  const handleSaveEvidences = useCallback((evidences: PlanEvidence[]) => {
    const hasEvidences = evidences.some(
      (evidence) => evidence.files.length > 0 || evidence.comments.length > 0
    )
    const newIncludes = hasEvidences
      ? Array.from(new Set([...data.includes, 'evidences']))
      : data.includes.filter((id) => id !== 'evidences')

    onChange({ evidences, includes: newIncludes as PlanIncludeOption[] })
  }, [data.includes, onChange])

  const handleSaveSolverEvidences = useCallback((solverEvidences: EvidenceData[]) => {
    const hasSolverEvidences = solverEvidences.some((item) =>
      Object.values(item ?? {}).some((evidence) => evidence?.active)
    )
    const newIncludes = hasSolverEvidences
      ? Array.from(new Set([...data.includes, 'solverEvidences']))
      : data.includes.filter((id) => id !== 'solverEvidences')

    onChange({ solverEvidences, includes: newIncludes as PlanIncludeOption[] })
  }, [data.includes, onChange])

  if (activeConfig === 'items') {
    return (
      <ConfigureItems
        items={data.items}
        onSave={(items) => {
          handleSaveItems(items)
          onExitConfig()
        }}
        onBack={onExitConfig}
      />
    )
  }

  if (activeConfig === 'linkQuestionnaire') {
    return (
      <ConfigureLinkQuestionnaire
        data={data.linkQuestionnaire}
        onSave={(linkQuestionnaire) => {
          handleSaveLinkQuestionnaire(linkQuestionnaire)
          onExitConfig()
        }}
        onBack={onExitConfig}
      />
    )
  }

  if (activeConfig === 'evidences') {
    return (
      <ConfigureEvidences
        evidences={data.evidences}
        onSave={(evidences) => {
          handleSaveEvidences(evidences)
          onExitConfig()
        }}
        onBack={onExitConfig}
      />
    )
  }

  if (activeConfig === 'solverEvidences') {
    return (
      <ConfigureSolverEvidences
        data={data.solverEvidences}
        onSave={(solverEvidences) => {
          handleSaveSolverEvidences(solverEvidences)
          onExitConfig()
        }}
        onBack={onExitConfig}
      />
    )
  }

  return (
    <Box sx={styles.content}>
      <Box>
        <Typography sx={styles.sectionTitle}>{t('actionPlan.manualCreation.step1.title')}</Typography>
        <Typography sx={styles.sectionSubtitle}>{t('actionPlan.manualCreation.step1.subtitle')}</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <BaseInput
            name='title'
            value={{
              title: {
                label: t('actionPlan.manualCreation.step1.titleLabel'),
                value: data.title,
              },
            }}
            onChange={handleTitleChange}
            errors={errors}
            fullWidth
            required
          />

          <Box sx={styles.instructionsField}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              label={t('actionPlan.manualCreation.step1.instructions')}
              value={data.instructions}
              onChange={handleInstructionsChange}
              inputProps={{ maxLength: 300 }}
              helperText={`${data.instructions.length}/300 ${t('actionPlan.manualCreation.step1.charactersAvailable')}`}
              sx={styles.fullWidth}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <DateModalInput
            label={t('actionPlan.manualCreation.step1.endDateLabel')}
            defaultDate={data.endDate ? dayjs(data.endDate) : null}
            getDate={handleEndDateChange}
            error={!!errors.endDate}
            helperText={errors.endDate}
            payload={{ min: minDate }}
            title='SELECCIÓN DE FECHA Y HORA DE FIN'
            name='endDate'
            required
          />

          <Box sx={styles.toggleContainer}>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={data.sendEmailCopy}
                  onChange={handleSendEmailCopyChange}
                />
              }
              label=''
            />
            <Typography sx={styles.toggleLabel}>
              {t('actionPlan.manualCreation.step1.sendEmailCopy')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={styles.whatIncludesSection}>
        <Typography sx={styles.sectionTitle}>{t('actionPlan.manualCreation.whatIncludes.title')}</Typography>
        <Typography sx={styles.helperText}>{t('actionPlan.manualCreation.whatIncludes.helper')}</Typography>

        <Grid container spacing={2} sx={{ marginTop: '4px' }}>
          {PLAN_INCLUDE_OPTIONS.map((option) => {
            const isConfigured =
              (option.id === 'items' && isItemsConfigured) ||
              (option.id === 'linkQuestionnaire' && isLinkQuestionnaireConfigured) ||
              (option.id === 'evidences' && isEvidencesConfigured) ||
              (option.id === 'solverEvidences' && isSolverEvidencesConfigured)
            const isClickable =
              option.id === 'items' ||
              option.id === 'linkQuestionnaire' ||
              option.id === 'evidences' ||
              option.id === 'solverEvidences'
            const title = t(`actionPlan.manualCreation.whatIncludes.options.${option.translationIndex}.title`)
            const description = t(`actionPlan.manualCreation.whatIncludes.options.${option.translationIndex}.description`)

            return (
              <Grid item xs={12} md={6} key={option.id}>
                <Box
                  role={isClickable ? 'button' : undefined}
                  aria-pressed={isClickable ? isConfigured : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={() => isClickable && handleEnterConfig(option.id)}
                  onKeyDown={(event) => {
                    if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault()
                      handleEnterConfig(option.id)
                    }
                  }}
                  sx={[styles.card]}
                >
                  {isConfigured && (
                    <Box sx={styles.checkIcon}>{icons('simple-check')}</Box>
                  )}

                  <Box sx={styles.cardContent}>
                    <Typography sx={styles.cardTitle}>{title}</Typography>
                    <Typography sx={styles.cardDescription}>{description}</Typography>
                  </Box>

                  <Box sx={styles.cardActions}>
                    <Box sx={styles.cardArrow}>{icons('arrow-right')}</Box>

                    {isConfigured && (
                      <Box
                        role='button'
                        tabIndex={0}
                        sx={styles.deleteIcon}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleOpenClearModal(option.id)
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            event.stopPropagation()
                            handleOpenClearModal(option.id)
                          }
                        }}
                      >
                        {icons('delete')}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            )
          })}
        </Grid>
      </Box>

      <SimpleModal
        sx={{ width: '400px', borderRadius: '10px' }}
        open={!!clearModalOption}
        close={handleCloseClearModal}
        back={handleCloseClearModal}
        next={handleConfirmClear}
        title={t('actionPlan.manualCreation.clearConfiguration.title')}
        description={t('actionPlan.manualCreation.clearConfiguration.description')}
        cancelText={t('actionPlan.manualCreation.clearConfiguration.cancel')}
        buttonText={t('actionPlan.manualCreation.clearConfiguration.confirm')}
      />
    </Box>
  )
}

export default Step1BasicInfo
